export enum OrderStatus {
  CANCELED = 'CANCELED',
  DENIED = 'DENIED',
  FAILED = 'FAILED',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  AWAITING_SAMPLE = 'AWAITING_SAMPLE',
  ANALYZING = 'ANALYZING',
  REFERRAL_LAB_LD_REVIEW = 'REFERRAL_LAB_LD_REVIEW',
  CLINICIAN_REVIEW = 'CLINICIAN_REVIEW',
  COMPLETED = 'COMPLETED',
  REPORTED = 'REPORTED',
}

export enum ExternalOrderStatus {
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  DENIED = 'DENIED',
  AWAITING_SAMPLE = 'AWAITING_SAMPLE',
  ANALYZING = 'ANALYZING',
  REFERRAL_LAB_LD_REVIEW = 'REFERRAL_LAB_LD_REVIEW',
  CLINICIAN_REVIEW = 'CLINICIAN_REVIEW',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
  FAILED = 'FAILED',
}

export enum DisplayableOrderStatus {
  RECEIVED = 'RECEIVED',
  LAB_PROCESSING = 'LAB_PROCESSING',
  CLINICIAN_REVIEW = 'CLINICIAN_REVIEW',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
}

export const ORDER_TIMELINE_STATUSES = {
  ORDERED: 'ORDERED',
  SAMPLE_RECEIVED: 'SAMPLE_RECEIVED',
  LAB_PROCESSING: 'LAB_PROCESSING',
  REPORTED: 'REPORTED',
  CLINICIAN_REVIEWED: 'CLINICIAN_REVIEWED',
  COMPLETED: 'COMPLETED',
  CANCELED: 'CANCELED',
} as const;

export type OrderTimelineStatus = EnumObjectValues<typeof ORDER_TIMELINE_STATUSES>;

export enum OrderResultFinding {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
  AVERAGE_RISK = 'AVERAGE_RISK',
  ELEVATED_RISK = 'ELEVATED_RISK',
  MINIMAL_IMPACT = 'MINIMAL_IMPACT',
  ALTERED_DOSE = 'ALTERED_DOSE',
  MAJOR_IMPACT = 'MAJOR_IMPACT',
  CONTRAINDICATED = 'CONTRAINDICATED',
  HIGH_RISK = 'HIGH_RISK',
  INTERMEDIATE_RISK = 'INTERMEDIATE_RISK',
  BORDERLINE_RISK = 'BORDERLINE_RISK',
  LOW_RISK = 'LOW_RISK',
  // APOE diplotype risk relative to the e3/e3 reference genotype.
  // NOT_REPORTABLE means genotyping succeeded but the call is outside e2/e3/e4.
  // NOT_ANALYZED means quality control did not pass and no genotype was established.
  INCREASED_RISK = 'INCREASED_RISK',
  NOT_INCREASED_RISK = 'NOT_INCREASED_RISK',
  DECREASED_RISK = 'DECREASED_RISK',
  UNCERTAIN = 'UNCERTAIN',
  NOT_REPORTABLE = 'NOT_REPORTABLE',
  NOT_ANALYZED = 'NOT_ANALYZED',
}

/** APOE haplotypes written in ascending order. The e1 haplotype is not called. */
export enum APOEDiplotype {
  E2_E2 = 'e2/e2',
  E2_E3 = 'e2/e3',
  E2_E4 = 'e2/e4',
  E3_E3 = 'e3/e3',
  E3_E4 = 'e3/e4',
  E4_E4 = 'e4/e4',
}

export const APOE_RISK_CATEGORIES = [
  OrderResultFinding.DECREASED_RISK,
  OrderResultFinding.AVERAGE_RISK,
  OrderResultFinding.INCREASED_RISK,
  OrderResultFinding.NOT_REPORTABLE,
  OrderResultFinding.NOT_ANALYZED,
] as const;

export type APOERiskCategory = (typeof APOE_RISK_CATEGORIES)[number];

/** Quality flag from the APOE genotyping module. PASS is sent alone when no other flag applies. */
export enum APOEQCFlag {
  PASS = 'PASS',
  LOW_DEPTH = 'LOW_DEPTH',
  AMBIGUOUS_ALLELE_BALANCE = 'AMBIGUOUS_ALLELE_BALANCE',
  NO_SUPPORTED_DIPLOTYPE = 'NO_SUPPORTED_DIPLOTYPE',
  PHASING_SUGGESTS_UNSUPPORTED_HAPLOTYPE = 'PHASING_SUGGESTS_UNSUPPORTED_HAPLOTYPE',
  PHASING_NOT_PERFORMED = 'PHASING_NOT_PERFORMED',
}

export interface OrderResult {
  description: string;
  uri: string;
  mimetype: string;
}

export interface ToplineResult {
  finding: OrderResultFinding;
  text: string;
  details?: string;
  /** Called APOE diplotype when this topline is an APOE genotyping result. */
  diplotype?: APOEDiplotype;
  /** Quality flags from the APOE genotyping module. */
  qc_flags?: APOEQCFlag[];
}

export interface Order {
  created_at: string;
  order_id: string;
  product_id: string;
  product_name: string;
  product_family_name?: string | null;
  product_test_name?: string | null;
  product_test_type?: string | null;
  product_test_subclass?: string | null;
  /** API report type. APOE genotyping is reported as EMERGING_GENES. */
  report_type?: string;
  status: OrderStatus;
  results?: ToplineResult[];
  reported_at?: string;
  canceled_at?: string;
}

export interface ExtendedOrder extends Order {
  isReported: boolean;
}

export enum ResultsType {
  ACTIONABLE_RESULT = 'ACTIONABLE_RESULT',
  AVERAGE_RISK_OR_NEGATIVE = 'AVERAGE_RISK_OR_NEGATIVE',
  NO_DATA_AVAILABLE = 'NO_DATA_AVAILABLE',
}
